import type { intlFormatPropTypes } from "react-intl";
import { z } from "zod";

import {
  MAX_DAY_RATE_DISCOUNT_PERCENT,
  MAX_HOURLY_RATE,
  MAX_MINIMUM_DURATION_HOURS,
  MIN_MINIMUM_DURATION_HOURS,
} from "../ActivateListingsPage.consts";

type BuildActivateListingSchemaArgs = {
  intl: intlFormatPropTypes;
  requiredAmenityIds: string[];
  isInstantBookEligible: boolean;
};

const toNumberOrZero = (val: unknown) => {
  const num = Number(val);
  return Number.isNaN(num) ? 0 : num;
};

const requiredNumber = (
  min: number,
  minMessage: string,
  max?: number,
): z.ZodType<number, z.ZodTypeDef, number> => {
  const schema = z.number().min(min, minMessage);
  return z.preprocess(
    toNumberOrZero,
    max === undefined ? schema : schema.max(max),
  ) as unknown as z.ZodType<number, z.ZodTypeDef, number>;
};

const buildActivateListingFormSchema = ({
  intl,
  requiredAmenityIds,
  isInstantBookEligible,
}: BuildActivateListingSchemaArgs) =>
  z
    .object({
      hourlyRate: requiredNumber(
        1,
        intl.formatMessage({
          id: "hosting.activate_listings.hourly_rate_required",
          defaultMessage: "Please enter an hourly rate",
        }),
        MAX_HOURLY_RATE,
      ),
      minimumDuration: requiredNumber(
        MIN_MINIMUM_DURATION_HOURS,
        intl.formatMessage({
          id: "hosting.activate_listings.minimum_duration_required",
          defaultMessage: "Please enter a minimum duration",
        }),
        MAX_MINIMUM_DURATION_HOURS,
      ),
      numberOfGuests: requiredNumber(
        1,
        intl.formatMessage({
          id: "hosting.activate_listings.capacity_required",
          defaultMessage: "Please enter a maximum number of guests",
        }),
      ),
      // Optional: 0 means no discount offered
      dayRateDiscountPercent: z.coerce
        .number()
        .min(0)
        .max(MAX_DAY_RATE_DISCOUNT_PERCENT)
        .optional(),
      cleaningFeeOption: z.enum(["INCLUDED", "FLAT_RATE"]),
      cleaningFeeValue: z.coerce.number().min(0).optional(),
      instantBookAvailability: z.enum(["EVERYONE", "NO_ONE"]).optional(),
      instantBookRequested: z.boolean(),
      amenities: z.record(z.boolean()),
      customAmenities: z.record(z.boolean()),
    })
    .superRefine((values, ctx) => {
      if (isInstantBookEligible && !values.instantBookAvailability) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["instantBookAvailability"],
          message: intl.formatMessage({
            id: "hosting.activate_listings.instant_book_required",
            defaultMessage: "Please select an option",
          }),
        });
      }

      if (
        values.cleaningFeeOption === "FLAT_RATE" &&
        !values.cleaningFeeValue
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["cleaningFeeValue"],
          message: intl.formatMessage({
            id: "hosting.activate_listings.cleaning_fee_invalid",
            defaultMessage: "Please enter a cleaning fee",
          }),
        });
      }

      const selectedAmenityIds = Object.entries(values.amenities)
        .filter(([, isSelected]) => isSelected)
        .map(([id]) => id);

      if (selectedAmenityIds.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["amenities"],
          message: intl.formatMessage({
            id: "hosting.activate_listings.amenities_required",
            defaultMessage: "Please select at least one amenity",
          }),
        });
      }

      requiredAmenityIds.forEach((id) => {
        const isSelected = selectedAmenityIds.includes(id);
        if (!isSelected) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`amenities.${id}`],
            message: intl.formatMessage({
              id: "hosting.activate_listings.required_amenities_missing",
              defaultMessage: "This amenity is required for your listing",
            }),
          });
        }
      });
    });

type ActivateListingFormValues = z.infer<
  ReturnType<typeof buildActivateListingFormSchema>
>;

export { buildActivateListingFormSchema };
export type { ActivateListingFormValues };
